import { Result, ResultSummary, RoundResult } from "./reducer";

export type InsightsRoundAccuracy = {
  round: number;
  beer: string;
  correct: number;
};

export type InsightsHalf = {
  correct: number;
  total: number;
  pct: number;
};

export type InsightsConfusion = {
  round: number;
  actual: string;
  guessed: string;
  times: number;
};

export type InsightsMover = {
  userEmail: string;
  firstHalf: number;
  secondHalf: number;
  delta: number;
};

export type Insights = {
  tastersCount: number;
  roundsCount: number;
  totalGuesses: number;
  correctGuesses: number;
  accuracyPct: number;
  roundAccuracy: InsightsRoundAccuracy[];
  toughestRounds: InsightsRoundAccuracy[];
  easiestRound: InsightsRoundAccuracy | null;
  halves: { first: InsightsHalf; second: InsightsHalf };
  asterisks: { used: number; correct: number; wrong: number; netPoints: number };
  decisiveness: { firstAvg: number; secondAvg: number };
  confusion: InsightsConfusion[];
  comeback: InsightsMover | null;
  collapse: InsightsMover | null;
  correctOrder: string[];
};

const emptyInsights: Insights = {
  tastersCount: 0,
  roundsCount: 0,
  totalGuesses: 0,
  correctGuesses: 0,
  accuracyPct: 0,
  roundAccuracy: [],
  toughestRounds: [],
  easiestRound: null,
  halves: {
    first: { correct: 0, total: 0, pct: 0 },
    second: { correct: 0, total: 0, pct: 0 },
  },
  asterisks: { used: 0, correct: 0, wrong: 0, netPoints: 0 },
  decisiveness: { firstAvg: 0, secondAvg: 0 },
  confusion: [],
  comeback: null,
  collapse: null,
  correctOrder: [],
};

const avg = (nums: number[]): number => {
  if (!nums.length) return 0;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
};

const roundTo = (n: number, decimals = 1): number => {
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
};

const halfRanges = (rounds: number): { first: number[]; second: number[] } => {
  const half = Math.floor(rounds / 2);
  return {
    first: Array.from({ length: half }, (_, i) => i),
    second: Array.from({ length: rounds - half }, (_, i) => half + i),
  };
};

export function buildInsights(result?: Result | null): Insights {
  const tasters: ResultSummary[] = result?.roundResults ?? [];
  const tastersCount = tasters.length;
  if (!tastersCount) return emptyInsights;

  const firstTaster = tasters[0];
  const roundsCount = firstTaster?.roundResults?.length ?? 0;
  if (!roundsCount) return { ...emptyInsights, tastersCount };

  const allRounds: RoundResult[] = tasters.flatMap(
    (t) => t.roundResults ?? []
  );

  const totalGuesses = tastersCount * roundsCount;
  const correctGuesses = allRounds.filter((r) => r.correct).length;
  const accuracyPct =
    totalGuesses > 0 ? (correctGuesses / totalGuesses) * 100 : 0;

  // Accuracy by round
  const roundAccuracy: InsightsRoundAccuracy[] = [];
  for (let i = 0; i < roundsCount; i++) {
    const rows = allRounds.filter((r) => r.index === i);
    const correct = rows.filter((r) => r.correct).length;
    const beer = rows[0]?.correctBeer ?? "—";
    roundAccuracy.push({ round: i + 1, beer, correct });
  }
  const toughestRounds = roundAccuracy.filter((r) => r.correct === 0);
  const easiestRound =
    roundAccuracy.slice().sort((a, b) => b.correct - a.correct)[0] ?? null;

  // Halves
  const { first, second } = halfRanges(roundsCount);
  const halfStats = (rounds: number[]): InsightsHalf => {
    const rows = allRounds.filter((r) => rounds.includes(r.index));
    const total = rows.length;
    const correct = rows.filter((r) => r.correct).length;
    const pct = total > 0 ? (correct / total) * 100 : 0;
    return { correct, total, pct: roundTo(pct, 1) };
  };
  const halves = { first: halfStats(first), second: halfStats(second) };

  // Asterisks
  const astRows = allRounds.filter((r) => r.asterisked);
  const astCorrect = astRows.filter((r) => r.correct).length;
  const astWrong = astRows.length - astCorrect;
  const asterisks = {
    used: astRows.length,
    correct: astCorrect,
    wrong: astWrong,
    netPoints: astCorrect - astWrong,
  };

  // Decisiveness (avg changesMade in each half across all tasters)
  const firstChanges = allRounds
    .filter((r) => first.includes(r.index))
    .map((r) => r.changesMade ?? 0);
  const secondChanges = allRounds
    .filter((r) => second.includes(r.index))
    .map((r) => r.changesMade ?? 0);
  const decisiveness = {
    firstAvg: roundTo(avg(firstChanges), 2),
    secondAvg: roundTo(avg(secondChanges), 2),
  };

  // Confusion: per round, most common wrong guess — keep top 3 overall
  const confusion: InsightsConfusion[] = [];
  for (let i = 0; i < roundsCount; i++) {
    const wrongs = allRounds.filter((r) => r.index === i && !r.correct);
    if (!wrongs.length) continue;
    const actual = wrongs[0].correctBeer;
    const counts: { [beer: string]: number } = {};
    wrongs.forEach((r) => {
      if (!r.selectedBeer) return;
      counts[r.selectedBeer] = (counts[r.selectedBeer] ?? 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (top) {
      confusion.push({ round: i + 1, actual, guessed: top[0], times: top[1] });
    }
  }
  confusion.sort((a, b) => b.times - a.times);

  // Comeback / Collapse
  const movers: InsightsMover[] = tasters.map((t) => {
    const rounds = t.roundResults ?? [];
    const firstCorrect = rounds.filter(
      (r) => first.includes(r.index) && r.correct
    ).length;
    const secondCorrect = rounds.filter(
      (r) => second.includes(r.index) && r.correct
    ).length;
    return {
      userEmail: t.userEmail,
      firstHalf: firstCorrect,
      secondHalf: secondCorrect,
      delta: secondCorrect - firstCorrect,
    };
  });
  movers.sort((a, b) => b.delta - a.delta);
  const comeback = movers[0] && movers[0].delta > 0 ? movers[0] : null;
  const collapse =
    movers.length > 0 && movers[movers.length - 1].delta < 0
      ? movers[movers.length - 1]
      : null;

  // Correct order from the first taster's per-round correctBeer
  const correctOrder =
    firstTaster?.roundResults
      ?.slice()
      ?.sort((a, b) => a.index - b.index)
      ?.map((r) => r.correctBeer) ?? [];

  return {
    tastersCount,
    roundsCount,
    totalGuesses,
    correctGuesses,
    accuracyPct: roundTo(accuracyPct, 1),
    roundAccuracy,
    toughestRounds,
    easiestRound,
    halves,
    asterisks,
    decisiveness,
    confusion: confusion.slice(0, 3),
    comeback,
    collapse,
    correctOrder,
  };
}
